/// <amd-module name="VSS/Features/MonacoWidgets/Widgets/LinkWidget" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { ObservableValue } from "VSS/Core/Observable";
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import * as Resources from "VSS/Features/MonacoWidgets/Resources";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";

export interface IContextMenuItem {
    text: string;
    onClickRef: string;
    contextMenuGroup: string;
}

interface IContributedLanguageProvider {
    availableLanguages: string[];
    contextMenuItems: IContextMenuItem[];
}

/**
 * Gets a widget used to copy a URL to the current selection to the clipboard
 */
export function getLinkWidgetRegistration(getLink: (selection: monaco.IRange, editorType?: number) => string, pageContext?: IVssPageContext, fileExtension?: string, addToOriginalDiff?: boolean): IEditorWidgetRegistration<LinkWidget> {
    return {
        addWidget: (editor: monaco.editor.IStandaloneCodeEditor) => {
            const linkWidget = new LinkWidget(editor, getLink, pageContext, fileExtension, addToOriginalDiff);
            editor.addContentWidget(linkWidget);
            linkWidget.addContextMenuItems();
            return linkWidget;
        },
        removeWidget: (widget, editor) => {
            widget.dispose();
            editor.removeContentWidget(widget);
        },
        widgetId: "link-widget"
    };
}

class LinkWidget implements monaco.editor.IContentWidget {
    public static getId(editor: monaco.editor.IStandaloneCodeEditor, addToOriginalDiff?: boolean): string {
        return `${editor.getId()}:${addToOriginalDiff ? "Original" : "Modified"} :${"LinkWidget"}`;
    }

    private editor: monaco.editor.IStandaloneCodeEditor;
    private domNode: HTMLElement;
    private disposables: monaco.IDisposable[] = [];
    private selection: monaco.Selection | undefined;
    private delay: number;
    private getLink: (selection: monaco.IRange, editorType?: number) => string;
    private tooltipText = new ObservableValue(Resources.CopyLinkTooltip);
    private pageContext: IVssPageContext | undefined;
    private extensionContribution: IContributedLanguageProvider | undefined;
    private fileExtension: string | undefined;
    private addToOriginalDiff: boolean | undefined;

    constructor(editor: monaco.editor.IStandaloneCodeEditor, getLink: (selection: monaco.IRange, editorType?: number) => string, pageContext?: IVssPageContext, fileExtension?: string, addToOriginalDiff?: boolean) {
        this.editor = editor;
        this.getLink = getLink;
        this.pageContext = pageContext;
        this.fileExtension = fileExtension;

        //Adding "Copy link command" to Monaco F1 palette to make this widget reachable from everywhere
        this.editor.addAction({
            id: 'copy-link-to-code-selection-widjet',
            label: 'Copy link to code selection',
            precondition: undefined,
            keybindingContext: undefined,
            contextMenuGroupId: 'code_selection',
            contextMenuOrder: 1.5,

            run: () => this.onButtonClick(),
        });

        this.disposables.push(
            this.editor.onDidChangeCursorSelection(e => {
                this.setSelection(undefined);

                const selectionIsEmpty =
                    !e.selection || (e.selection.endLineNumber === e.selection.startLineNumber && e.selection.endColumn === e.selection.startColumn);
                if (!selectionIsEmpty) {
                    this.setSelection(e.selection);
                }
            })
        );

        this.addToOriginalDiff = addToOriginalDiff;
    }

    public async addContextMenuItems() {
        const languageByExt: { [key: string]: string } = {
            "cpp": "cpp",
            "cs": "csharp",
            "vb": "csharp",
            "h": "cpp",
            "java": "java",
            "js": "javascript",
            "jsx": "javascript",
            "ts": "typescript",
            "tsx": "typescript",
        };

        if (!this.extensionContribution && this.pageContext && this.fileExtension) {
            const extensionContributionType = "ms.vss-features.code-editor-navigation-provider";
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const providerContributions = contributionService.getContributionsByType(extensionContributionType);
            for (const providerContributionId in providerContributions) {
                const extensionContributionResponse: IContributedLanguageProvider | undefined = await contributionService.getService(providerContributionId);
                this.extensionContribution = { availableLanguages: extensionContributionResponse?.availableLanguages!, contextMenuItems: extensionContributionResponse?.contextMenuItems! };
            }

            if (this.extensionContribution?.availableLanguages.indexOf(languageByExt[this.fileExtension]) != -1) {
                this.extensionContribution?.contextMenuItems.forEach(menuItem => {
                    this.editor.addAction({
                        id: menuItem.text.toLowerCase().replace(/\s/g, "-"),
                        label: menuItem.text,
                        precondition: undefined,
                        keybindingContext: undefined,
                        contextMenuGroupId: menuItem.contextMenuGroup,
                        contextMenuOrder: 100.5,

                        run: () => this.onMenuItemClick(menuItem.onClickRef),
                    });
                });
            }
        }

    }

    private onMenuItemClick = (onClickRef: string) => {
        window.open(onClickRef);
    };

    public getId(): string {
        return LinkWidget.getId(this.editor, this.addToOriginalDiff);
    }

    public getDomNode(): HTMLElement {
        if (!this.domNode) {
            this.domNode = document.createElement("div");
            ReactDOM.render(
                <Observer tooltipText={this.tooltipText}>
                    {(props: { tooltipText: string }) => {
                        return (
                            <Button
                                className="copy-link-widget"
                                subtle={true}
                                iconProps={{ iconName: "Link" }}
                                onClick={this.onButtonClick}
                                tooltipProps={{ text: props.tooltipText }}
                            />
                        );
                    }}
                </Observer>,
                this.domNode
            );
        }

        return this.domNode;
    }

    public getPosition(): monaco.editor.IContentWidgetPosition {
        if (this.selection) {
            const selectionIsUpward =
                this.selection.positionLineNumber < this.selection.selectionStartLineNumber ||
                (this.selection.positionLineNumber === this.selection.selectionStartLineNumber &&
                    this.selection.positionColumn < this.selection.selectionStartColumn);

            return {
                position: {
                    column: this.selection.positionColumn,
                    lineNumber: this.selection.positionLineNumber
                },
                preference: selectionIsUpward
                    ? [monaco.editor.ContentWidgetPositionPreference.ABOVE, monaco.editor.ContentWidgetPositionPreference.BELOW]
                    : [monaco.editor.ContentWidgetPositionPreference.BELOW, monaco.editor.ContentWidgetPositionPreference.ABOVE]
            };
        }

        return null as any;
    }

    public dispose() {
        ReactDOM.unmountComponentAtNode(this.domNode);
        this.disposables.forEach(d => d.dispose());
        this.disposables = [];
    }

    private onButtonClick = () => {
        if (this.selection) {
            const type = this.editor.getContainerDomNode().classList.contains("original") ? 1 : 2;
            let link = this.getLink({
                startLineNumber: this.selection.startLineNumber,
                endLineNumber: this.selection.endLineNumber,
                startColumn: this.selection.startColumn,
                endColumn: this.selection.endColumn
            }, type);

            copyToClipboard(link);

            this.tooltipText.value = Resources.CopyLinkCompletedTooltip;
        }
    };

    private setSelection(selection: monaco.Selection | undefined) {
        this.selection = selection;
        if (this.delay) {
            clearTimeout(this.delay);
        }
        this.domNode.hidden = true;
        if (this.selection && this.editor.getOption(monaco.editor.EditorOption.readOnly)) {
            this.delay = setTimeout(() => {
                if (this.tooltipText.value !== Resources.CopyLinkTooltip) {
                    this.tooltipText.value = Resources.CopyLinkTooltip;
                }
                this.domNode.hidden = false;
                this.editor.layoutContentWidget(this);
            }, 250);
        }
    }
}
